import type { CasesSubmittedTrend } from '@compass/client/types/cases_analytics';
import React, { useMemo, useState } from 'react';

import { ChartCard } from '@/pages/analytics/components/ChartCard';
import { ChartRenderBoundary } from '@/pages/analytics/components/ChartRenderBoundary';
import { ChartBodySkeleton } from '@/pages/analytics/components/ChartStates';
import { ScopeToggle } from '@/pages/analytics/components/ScopeToggle';
import { StackedBarChart } from '@/pages/analytics/components/StackedBarChart';
import type { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import type { TrendBucket } from '@/pages/analytics/types';
import { formatChartAxisDate, formatChartTooltipDate } from '@/pages/analytics/utils/chartDate';
import { COLOR_MUTED } from '@/pages/analytics/utils/chartPalette';
import { formatBucketLabel } from '@/pages/analytics/utils/dateWindow';
import { normalizeSubmittedTrend } from '@/pages/analytics/utils/normalizeChartRows';
import { foldRowsTopN, OTHER_SLICE_KEY } from '@/pages/analytics/utils/series';

import { type Grouping, GROUPING_OPTIONS } from '../utils/groupings';

interface SubmittedTrendCardProps {
  data?: CasesSubmittedTrend;
  trendBucket: TrendBucket;
  /** Chart labels of every bucket sent, so empty buckets still get a bar slot. */
  bucketDates: string[];
  /** Chart labels of buckets cut by the range (marked `*`). */
  partialDates: Set<string>;
  colors: Map<string, string>;
  download: ReturnType<typeof useDownloadChart>;
  loading?: boolean;
}

const HEIGHT = 280;
/** The chart keeps the five largest groups and folds the rest into Other; downloads stay unfolded. */
const TOP_N = 5;

export const SubmittedTrendCard: React.FC<SubmittedTrendCardProps> = ({
  data,
  trendBucket,
  bucketDates,
  partialDates,
  colors,
  download,
  loading,
}) => {
  const [grouping, setGrouping] = useState<Grouping>('by_report_type');
  const points = data?.[grouping];
  const rows = useMemo(
    () =>
      foldRowsTopN(
        (points ?? []).map(p => ({
          x: p.date ?? '',
          group: p.group ?? '',
          group_name: p.group_name,
          value: p.case_count ?? 0,
        })),
        TOP_N,
      ),
    [points],
  );
  // The folded slice is not a real group, so the page-wide colour map has no entry for it.
  const chartColors = useMemo(() => new Map([...colors, [OTHER_SLICE_KEY, COLOR_MUTED]]), [colors]);

  return (
    <ChartCard
      title="Cases submitted over time"
      subtitle={`Number of submitted cases per ${trendBucket}, by the date the call came in`}
      rightSlot={<ScopeToggle value={grouping} onChange={setGrouping} options={GROUPING_OPTIONS} />}
      onDownload={(format, exportElement) =>
        data &&
        download(
          normalizeSubmittedTrend(points ?? [], grouping),
          format,
          'Cases submitted over time',
          exportElement,
        )
      }
      downloadDisabled={!data}
    >
      {loading ? (
        <ChartBodySkeleton height={HEIGHT} />
      ) : (
        <ChartRenderBoundary resetKeys={[points, grouping, trendBucket]} height={HEIGHT}>
          <StackedBarChart
            rows={rows}
            xValues={bucketDates}
            xFormatter={x =>
              formatBucketLabel(x, formatChartAxisDate(x, trendBucket), partialDates)
            }
            tooltipXFormatter={x =>
              formatBucketLabel(x, formatChartTooltipDate(x, trendBucket), partialDates)
            }
            valueFormatter={v => `${v.toLocaleString('en-US')} cases`}
            colorByKey={chartColors}
            ariaLabelPrefix="Cases submitted bar chart"
            height={HEIGHT}
          />
        </ChartRenderBoundary>
      )}
    </ChartCard>
  );
};
